// ROM handling that runs on the player's device only: N64 byte order normalisation and fingerprinting.
import { sha1Hex } from './hash.ts';
import { LIMITS, type Manifest } from './manifest.ts';

export type N64Order = 'z64' | 'v64' | 'n64';

/** N64 images start with 0x80371240 in big-endian (.z64) order; .v64 swaps 16-bit halves, .n64 is little-endian 32-bit. */
export function detectN64Order(b: Uint8Array): N64Order | null {
  if (b.length < 4) return null;
  const m = ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
  if (m === 0x80371240) return 'z64';
  if (m === 0x37804012) return 'v64';
  if (m === 0x40123780) return 'n64';
  return null;
}

/** Returns a big-endian (.z64) copy; the input is untouched. */
export function toZ64(b: Uint8Array, order: N64Order): Uint8Array {
  const out = new Uint8Array(b.length);
  if (order === 'z64') { out.set(b); return out; }
  if (order === 'v64') {
    const n = b.length & ~1;
    for (let i = 0; i < n; i += 2) { out[i] = b[i + 1]; out[i + 1] = b[i]; }
    if (n < b.length) out[n] = b[n];
    return out;
  }
  const n = b.length & ~3;
  for (let i = 0; i < n; i += 4) { out[i] = b[i + 3]; out[i + 1] = b[i + 2]; out[i + 2] = b[i + 1]; out[i + 3] = b[i]; }
  out.set(b.subarray(n), n);
  return out;
}

export interface RomCheck {
  bytes: Uint8Array;
  sha1: string;
  /** true: matches rom.sha1; false: mismatch; null: the manifest lists no fingerprint. */
  matches: boolean | null;
  /** N64 byte order of the file as given (null if not recognised or not N64). */
  order: N64Order | null;
  converted: boolean;
  notes: string[];
}

export class RomTooBig extends Error {}

export async function checkRom(input: Uint8Array, fileName: string, m: Pick<Manifest, 'platform' | 'rom'>): Promise<RomCheck> {
  const max = Math.min(m.rom.maxSize ?? LIMITS.romBytes, LIMITS.romBytes);
  if (input.length > max) throw new RomTooBig(`${input.length} bytes is more than the ${max} bytes this game accepts`);
  const notes: string[] = [];
  let bytes = input;
  let order: N64Order | null = null;
  let converted = false;
  if (m.platform === 'n64') {
    order = detectN64Order(input);
    if (order && order !== 'z64' && (m.rom.byteswap ?? 'auto') === 'auto') {
      bytes = toZ64(input, order);
      converted = true;
      notes.push(`converted from .${order} byte order to .z64`);
    } else if (!order) notes.push('this file does not look like an N64 ROM');
  }
  const ext = (/\.[^.]+$/.exec(fileName)?.[0] ?? '').toLowerCase();
  if (ext && !m.rom.accept.includes(ext)) notes.push(`"${ext}" is not one of the expected extensions (${m.rom.accept.join(', ')})`);
  const sha1 = await sha1Hex(bytes);
  const list = m.rom.sha1 ?? [];
  return { bytes, sha1, matches: list.length ? list.includes(sha1) : null, order, converted, notes };
}
